'use client';

import { Link } from '@/i18n/navigation';
import {
  Check,
  X,
  Eye,
} from '@phosphor-icons/react';
import { useTranslations } from 'next-intl';
import { trackEvent } from '@/lib/analytics/matomo';

const FEATURE_KEYS = ['aiChat', 'healthRecord', 'dogs', 'reminders', 'vetExport', 'whatsapp'] as const;
const FREE_HAS_IT: Record<string, 'yes' | 'partial' | 'no'> = {
  aiChat: 'partial',
  healthRecord: 'yes',
  dogs: 'yes',
  reminders: 'no',
  vetExport: 'no',
  whatsapp: 'no',
};

interface FreePremiumCompareProps {
  className?: string;
}

export default function FreePremiumCompare({ className = '' }: FreePremiumCompareProps) {
  const t = useTranslations('landing.freePremiumCompare');

  return (
    <section
      className={`py-16 sm:py-24 px-4 sm:px-6 lg:px-8 ${className}`}
      aria-labelledby="pricing-compare-heading"
    >
      <div className="max-w-3xl mx-auto">
        <h2
          id="pricing-compare-heading"
          className="font-heading text-2xl sm:text-3xl font-semibold text-center text-dark mb-3"
        >
          {t('heading')}
        </h2>
        <p className="text-center text-gray-600 mb-10 max-w-lg mx-auto">
          {t('subheading')}
        </p>

        {/* Comparison table */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          {/* Free column */}
          <div className="bg-surface rounded-card p-6 shadow-card border border-border">
            <h3 className="font-heading font-semibold text-dark text-lg mb-1">{t('free')}</h3>
            <p className="text-sm text-gray-500 mb-6">{t('freeForever')}</p>
            <ul className="space-y-4" role="list">
              {FEATURE_KEYS.map((key) => {
                const freeStatus = FREE_HAS_IT[key];
                const hasFreeText = freeStatus !== 'no';
                return (
                  <li key={key} className="flex items-start gap-3 text-sm">
                    {freeStatus === 'no' ? (
                      <X weight="bold" className="w-4 h-4 text-gray-300 flex-shrink-0 mt-0.5" aria-hidden="true" />
                    ) : freeStatus === 'partial' ? (
                      <Eye weight="regular" className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
                    ) : (
                      <Check weight="bold" className="w-4 h-4 text-teal-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
                    )}
                    <div>
                      <span className="font-medium text-dark">{t(`features.${key}.feature`)}</span>
                      {hasFreeText ? (
                        <p className="text-gray-500 text-xs mt-0.5">{t(`features.${key}.free`)}</p>
                      ) : (
                        <p className="text-gray-400 text-xs mt-0.5">&mdash;</p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Plus column */}
          <div className="bg-surface rounded-card p-6 shadow-card border-2 border-teal-500 relative">
            <div className="absolute -top-3 left-6 bg-teal-500 text-white text-xs font-heading font-semibold px-3 py-1 rounded-full">
              {t('recommended')}
            </div>
            <h3 className="font-heading font-semibold text-dark text-lg mb-1">{t('plus')}</h3>
            <p className="text-sm text-gray-500 mb-6">
              {t('plusPrice')}
            </p>
            <ul className="space-y-4" role="list">
              {FEATURE_KEYS.map((key) => (
                <li key={key} className="flex items-start gap-3 text-sm">
                  <Check weight="bold" className="w-4 h-4 text-teal-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <div>
                    <span className="font-medium text-dark">{t(`features.${key}.feature`)}</span>
                    <p className="text-gray-500 text-xs mt-0.5">{t(`features.${key}.plus`)}</p>
                  </div>
                </li>
              ))}
            </ul>
            <p className="text-xs text-gray-500 mt-6 text-center">
              {t('annualNote')}
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-8">
          <Link
            href="/pricing"
            className="inline-block text-teal-500 hover:text-teal-600 font-heading font-semibold text-sm transition-colors"
            onClick={() => trackEvent('landing', 'pricing_preview_click')}
          >
            {t('seeFullPricing')} &rarr;
          </Link>
        </div>
      </div>
    </section>
  );
}
